// ============================================
// Chat Wrapper Component
// Conditionally renders chat for authenticated users
// ============================================

import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { ChatProvider } from './ChatProvider';
import ChatWidget from './ChatWidget';
import { getAnyValidToken } from '../../utils/authUtils';

// Platform detection - 'nacme' (default) or 'tla'
const PLATFORM = process.env.REACT_APP_PLATFORM || 'nacme';
const IS_TLA = PLATFORM === 'tla';

/**
 * Paths where chat should be available
 * TLA platform uses root paths, NACME uses prefixed paths
 */
const NACME_CHAT_PATHS = [
  '/student/dashboard',
  '/recruiter/dashboard',
  '/alumni/dashboard',
  '/alumni/directory',
  '/alumni/messages',
  '/alumni/groups',
  '/alumni/job-board',
  '/alumni/profile-management',
  '/admin'
];

const TLA_CHAT_PATHS = [
  '/dashboard',
  '/directory',
  '/messages',
  '/groups',
  '/job-board',
  '/profile-management'
];

const CHAT_ENABLED_PATHS = IS_TLA ? TLA_CHAT_PATHS : NACME_CHAT_PATHS;

/**
 * Check if current path should show chat
 */
const shouldShowChat = (pathname) => {
  return CHAT_ENABLED_PATHS.some(path =>
    pathname === path || pathname.startsWith(path + '/')
  );
};

/**
 * Get tenant based on platform and path
 * - TLA platform always uses 'tla' tenant
 * - NACME platform: /alumni/* uses 'tla', others use 'nacme'
 */
const getTenant = (pathname) => {
  if (IS_TLA) {
    return 'tla';
  }
  if (pathname.startsWith('/alumni')) {
    return 'tla';
  }
  return 'nacme';
};

/**
 * Chat Wrapper - provides chat context and widget for authenticated users
 */
const ChatWrapper = () => {
  const location = useLocation();
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [tenant, setTenant] = useState('nacme');

  // Check authentication and update tenant on location change
  useEffect(() => {
    const token = getAnyValidToken();
    setIsAuthenticated(!!token);
    setTenant(getTenant(location.pathname));
  }, [location.pathname]);

  // Don't render if not authenticated or not on a chat-enabled path
  if (!isAuthenticated || !shouldShowChat(location.pathname)) {
    return null;
  }

  return (
    <ChatProvider tenant={tenant} enabled={true}>
      <ChatWidget />
    </ChatProvider>
  );
};

export default ChatWrapper;
